/**
 * Company Financial Info Mock Data Generator
 *
 * 该模块提供企业KYC公司财务状况表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator.company-financial
 * @description 公司财务状况模拟数据生成器
 */

import { CompanyLegalPersonShareholderWealthSourceOptions } from "@/types/enum";
import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据
import countryAllDataJson from "../../data/country.json";
const countryAllData = countryAllDataJson as CountryJsonData;

/**
 * 财务信息表单数据类型
 */
export interface CompanyFinancialInfoFormData {
  /** 最近一个财年年度净利润 (USD) */
  netProfitLastFiscalYear: string;
  /** 资金来源国家/地区 */
  fundSourceCountry: string;
  /** 资金来源（多选）*/
  fundSource: string[];
  /** 其他资金来源说明 */
  otherFundSource?: string;
  /** 财富来源（多选）*/
  wealthSource: string[];
  /** 其他财富来源说明 */
  otherWealthSource?: string;
  /** 资产净值 (USD) */
  netAssets: string;
  /** 虚拟资产占净值比例 (%) */
  vaHoldingsRatio: string;
}

/**
 * Mock 数据生成配置选项
 */
export interface CompanyFinancialMockDataOptions {
  /** 自定义种子 */
  seed?: number;
  /** 自定义净利润 */
  netProfit?: number;
  /** 自定义资产净值 */
  netAssets?: number;
  /** 自定义虚拟资产占比 */
  vaHoldingsRatio?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 所有国家列表
 */
const ALL_COUNTRIES = countryAllData.data;

/**
 * 国家代码列表
 */
const ALL_COUNTRY_CODES = ALL_COUNTRIES.map((c) => c.code2);

/**
 * 财富来源选项（来自 strapi-enum.ts）
 */
const WEALTH_SOURCE_OPTIONS = Object.values(
  CompanyLegalPersonShareholderWealthSourceOptions
);

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成公司财务信息表单模拟数据
 *
 * @param options - 生成配置选项
 * @returns 符合表单验证规则的模拟数据
 *
 * @example
 * ```typescript
 * // 生成基本财务信息
 * const basicData = generateCompanyFinancialMockData();
 *
 * // 生成高净值企业数据
 * const highNetWorth = generateCompanyFinancialMockData({
 *   netProfit: 50000000,
 *   netAssets: 500000000,
 * });
 *
 * // 生成可重复的数据（用于测试）
 * const repeatableData = generateCompanyFinancialMockData({
 *   seed: 12345
 * });
 * ```
 */
export function generateCompanyFinancialMockData(
  options: CompanyFinancialMockDataOptions = {}
): CompanyFinancialInfoFormData {
  const {
    seed,
    netProfit: customNetProfit,
    netAssets: customNetAssets,
    vaHoldingsRatio: customVaHoldingsRatio,
  } = options;

  // 创建 faker 实例（如果提供种子，使用种子）
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  // 生成净利润：-100亿 到 1万亿 USD
  const netProfit =
    customNetProfit !== undefined
      ? customNetProfit
      : fakerInstance.number.int({ min: -10000000000, max: 1000000000000 });

  // 生成资产净值：0 到 1万亿 USD
  const netAssets =
    customNetAssets !== undefined
      ? customNetAssets
      : fakerInstance.number.int({ min: 0, max: 1000000000000 });

  // 生成虚拟资产占比：0-100%
  const vaHoldingsRatio =
    customVaHoldingsRatio !== undefined
      ? customVaHoldingsRatio
      : fakerInstance.number.int({ min: 0, max: 100 });

  // 选择资金来源国家
  const fundSourceCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);

  // 选择1-3个资金来源
  const fundSource = fakerInstance.helpers.arrayElements(
    WEALTH_SOURCE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  // 如果选择了"其他"，生成详细说明
  const otherFundSource = fundSource.includes(
    CompanyLegalPersonShareholderWealthSourceOptions.OTHER
  )
    ? fakerInstance.helpers.arrayElement([
        "来自母公司的注资支持",
        "政府项目补贴款项",
        "战略投资者的专项投资",
        "债券融资收入",
        "资产重组所得",
        "知识产权转让收入",
      ])
    : undefined;

  // 选择1-3个财富来源
  const wealthSource = fakerInstance.helpers.arrayElements(
    WEALTH_SOURCE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  // 如果选择了"其他"，生成详细说明
  const otherWealthSource = wealthSource.includes(
    CompanyLegalPersonShareholderWealthSourceOptions.OTHER
  )
    ? fakerInstance.helpers.arrayElement([
        "历年累计盈余公积",
        "资本公积转增",
        "品牌溢价收益",
        "技术许可费收入",
        "特许经营权收益",
        "商誉评估增值",
      ])
    : undefined;

  return {
    netProfitLastFiscalYear: netProfit.toString(),
    fundSourceCountry,
    fundSource,
    otherFundSource,
    wealthSource,
    otherWealthSource,
    netAssets: netAssets.toString(),
    vaHoldingsRatio: vaHoldingsRatio.toString(),
  };
}

/**
 * 批量生成财务信息表单模拟数据
 *
 * @param count - 生成数据的数量
 * @param options - 生成配置选项
 * @returns 模拟数据数组
 */
export function generateCompanyFinancialMockDataBatch(
  count: number,
  options: CompanyFinancialMockDataOptions = {}
): CompanyFinancialInfoFormData[] {
  return Array.from({ length: count }, (_, index) => {
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generateCompanyFinancialMockData(itemOptions);
  });
}

/**
 * 预设场景
 */
export const companyFinancialPresets = {
  /**
   * 小型盈利企业
   */
  smallProfitable: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 100000, max: 1000000 }),
      netAssets: faker.number.int({ min: 500000, max: 5000000 }),
      vaHoldingsRatio: faker.number.int({ min: 5, max: 15 }),
    }),

  /**
   * 中型盈利企业
   */
  mediumProfitable: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 5000000, max: 50000000 }),
      netAssets: faker.number.int({ min: 20000000, max: 200000000 }),
      vaHoldingsRatio: faker.number.int({ min: 10, max: 25 }),
    }),

  /**
   * 大型盈利企业
   */
  largeProfitable: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 100000000, max: 1000000000 }),
      netAssets: faker.number.int({ min: 500000000, max: 5000000000 }),
      vaHoldingsRatio: faker.number.int({ min: 15, max: 30 }),
    }),

  /**
   * 亏损企业
   */
  lossmaking: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: -10000000, max: -100000 }),
      netAssets: faker.number.int({ min: 1000000, max: 50000000 }),
      vaHoldingsRatio: faker.number.int({ min: 0, max: 10 }),
    }),

  /**
   * 高虚拟资产占比企业（接近30%监管上限）
   */
  highVaRatio: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 1000000, max: 50000000 }),
      netAssets: faker.number.int({ min: 10000000, max: 100000000 }),
      vaHoldingsRatio: faker.number.int({ min: 25, max: 30 }),
    }),

  /**
   * 低虚拟资产占比企业
   */
  lowVaRatio: (): CompanyFinancialInfoFormData =>
    generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 1000000, max: 100000000 }),
      netAssets: faker.number.int({ min: 10000000, max: 500000000 }),
      vaHoldingsRatio: faker.number.int({ min: 0, max: 5 }),
    }),

  /**
   * 包含其他资金来源（用于测试"其他"选项）
   */
  withOtherSources: (): CompanyFinancialInfoFormData => {
    const data = generateCompanyFinancialMockData({
      netProfit: faker.number.int({ min: 5000000, max: 50000000 }),
      netAssets: faker.number.int({ min: 20000000, max: 200000000 }),
      vaHoldingsRatio: faker.number.int({ min: 10, max: 25 }),
    });

    // 强制添加"其他"选项
    if (
      !data.fundSource.includes(
        CompanyLegalPersonShareholderWealthSourceOptions.OTHER
      )
    ) {
      data.fundSource.push(
        CompanyLegalPersonShareholderWealthSourceOptions.OTHER
      );
      data.otherFundSource = "来自母公司的注资支持";
    }
    if (
      !data.wealthSource.includes(
        CompanyLegalPersonShareholderWealthSourceOptions.OTHER
      )
    ) {
      data.wealthSource.push(
        CompanyLegalPersonShareholderWealthSourceOptions.OTHER
      );
      data.otherWealthSource = "历年累计盈余公积";
    }

    return data;
  },
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generateCompanyFinancialMockData,
  generateCompanyFinancialMockDataBatch,
  companyFinancialPresets,
};
